import React, { useMemo } from 'react';
import Points from 'components/Points';
import { TradeSuggestion, TradeSuggestionItem } from 'types/interfaces/tradeSuggestion';
import { getCardImageUrl } from 'utils/cardImageFallback';
import { identityLine } from './SuggestionLedger';
import SuggestionNet from './SuggestionNet';

/** The inbound side renders as a three-card fan: two flankers plus the hero. */
const FAN_SIZE = 3;

/** Sorts by PER-CARD value, never line total: four copies of a 50 point card do
 *  not outrank one 150 point card for hero selection. */
const byValueDescending = (items: TradeSuggestionItem[]) =>
  [...items].sort((a, b) => b.lockedPricePoints - a.lockedPricePoints);

const cardArt = (item: TradeSuggestionItem, size: 'small' | 'normal') =>
  getCardImageUrl({ image: item.card.imageUrl, scryfallId: item.card.scryfallId }, size);

interface CardCopy {
  item: TradeSuggestionItem;
  /** Unique per copy: the line id alone collides once a line renders N times. */
  key: string;
}

/**
 * One entry per physical copy: a line with quantity N yields N entries.
 *
 * Presentation only. The seed, the API types and everything reaching the offer
 * builder keep the quantity-per-line representation, so a multi-copy suggestion
 * still seeds one line with quantity N rather than N lines of one.
 *
 * Deliberate split from the ledger: the ledger stays ONE ROW PER LINE, showing
 * its quantity and line total, because expanding a four-of into four identical
 * rows would make the ledger unreadable. Only the card imagery expands. The two
 * treatments differ on purpose, not by oversight.
 */
const expandCopies = (items: TradeSuggestionItem[]): CardCopy[] =>
  items.flatMap((item) =>
    Array.from({ length: Math.max(1, item.quantity) }, (_, copyIndex) => ({
      item,
      key: `${item.id}-${copyIndex}`,
    }))
  );

/**
 * The trade axis, a three-column grid: YOU GIVE, the net, YOU GET.
 *
 * The centre column is what makes this read as a trade rather than two lists,
 * so it is not optional at any width — below 768px the three zones stack but
 * the net keeps its place between them.
 */
const SuggestionAxis: React.FC<{ suggestion: TradeSuggestion }> = ({ suggestion }) => {
  const { totals } = suggestion;

  const outbound = useMemo(
    () => expandCopies(byValueDescending(suggestion.youSend)),
    [suggestion.youSend]
  );
  const inbound = useMemo(
    () => expandCopies(byValueDescending(suggestion.theySend)),
    [suggestion.theySend]
  );

  // The fan renders hero-first in the DOM for z-index and focus order, but
  // paints as [flanker, hero, flanker]; CSS order places them. The three-card
  // fan plus a "+N more" count is the surface's EXISTING overflow behaviour for
  // a long inbound side; expansion only makes that count truthful about copies
  // rather than lines. No new truncation is introduced here.
  const fan = inbound.slice(0, FAN_SIZE);
  const hero = fan[0];
  const flankers = fan.slice(1);
  const inboundOverflow = inbound.length - fan.length;

  return (
    <div className="sugg-axis">
      <section className="sugg-axis-zone sugg-axis-zone--give" aria-label="You give">
        <div className="sugg-axis-label sugg-axis-label--give">YOU GIVE</div>
        <div className="sugg-axis-give-row">
          {outbound.map(({ item, key }) => (
            <img
              key={key}
              className="sugg-axis-give-art"
              src={cardArt(item, 'normal')}
              alt={item.card.name}
              loading="lazy"
            />
          ))}
        </div>
        <div className="sugg-axis-meta">
          <span className="sugg-axis-meta-count">
            {outbound.length} {outbound.length === 1 ? 'card' : 'cards'}
          </span>
          <Points type="small" points={totals.youSendPoints} />
        </div>
      </section>

      <section className="sugg-axis-zone sugg-axis-zone--net">
        <SuggestionNet suggestion={suggestion} />
      </section>

      <section className="sugg-axis-zone sugg-axis-zone--get" aria-label="You get">
        <div className="sugg-axis-label sugg-axis-label--get">YOU GET</div>
        <div className="sugg-axis-get-fan">
          {flankers.map(({ item, key }, index) => (
            <img
              key={key}
              className={`sugg-axis-get-flank sugg-axis-get-flank--${index === 0 ? 'left' : 'right'}`}
              src={cardArt(item, 'normal')}
              alt={item.card.name}
              loading="lazy"
            />
          ))}
          {hero && (
            <div className="sugg-axis-get-hero-wrap">
              <img
                className="sugg-axis-get-hero"
                src={cardArt(hero.item, 'normal')}
                alt={hero.item.card.name}
                loading="lazy"
              />
              <span className="sugg-axis-get-sheen" aria-hidden="true" />
            </div>
          )}
        </div>
        <div className="sugg-axis-meta sugg-axis-meta--get">
          {hero && <span className="sugg-axis-hero-name">{hero.item.card.name}</span>}
          {/* With more cards behind it the hero needs a count; alone, the space
              is better spent naming the exact printing being received. */}
          {inboundOverflow > 0 ? (
            <span className="sugg-axis-more">
              + {inboundOverflow} more {inboundOverflow === 1 ? 'card' : 'cards'}
            </span>
          ) : (
            hero && <span className="sugg-axis-more">{identityLine(hero.item)}</span>
          )}
          <Points type="small" points={totals.theySendPoints} />
        </div>
      </section>
    </div>
  );
};

export default SuggestionAxis;
